import { useCallback, useMemo } from 'react';
import { useRealtimePresence } from 'services';
import { usePresenceUsers } from 'components/PresenceUsers';
import { getUserTitle } from 'modules/core/helpers';
import type { CellSelectArgs, PresenceMarker } from 'components/DataGrid';
import type { GridSummaryRow, RowOrGroup } from '../types';
import { useBlockRealtime } from 'modules/campaign/block/BlockRealtimeProvider';
import { planPresence } from 'modules/campaign/block/realtimeDefinitions';

type PlanPresence = {
  markers: PresenceMarker[];
  onSelectedCellChange: (args: CellSelectArgs<RowOrGroup, GridSummaryRow> | null) => void;
};

/** Typed plan-domain presence on the shared block context channel. */
export function usePlanPresence(): PlanPresence {
  const { channelKey } = useBlockRealtime();
  const { participants, setPresence } = useRealtimePresence(channelKey, planPresence);
  const { color, user } = usePresenceUsers();

  const onSelectedCellChange = useCallback(
    (args: CellSelectArgs<RowOrGroup, GridSummaryRow> | null) => {
      setPresence(args?.row ? { rowKey: args.row._key, columnKey: args.column.key } : null);
    },
    [setPresence],
  );

  const markers = useMemo<PresenceMarker[]>(
    () =>
      participants
        .filter((participant) => participant.data !== null && !participant.isSelf)
        .map((participant) => {
          const data = participant.data!;
          const resolvedUser = user(participant.userId);
          return {
            rowKey: data.rowKey,
            columnKey: data.columnKey,
            color: color(participant.userId),
            title: resolvedUser ? getUserTitle(resolvedUser) : undefined,
          };
        }),
    [participants, user, color],
  );

  return { markers, onSelectedCellChange };
}
